import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import TabBar from '../components/TabBar';
import { BookOpen, Search, Clock, List, CheckSquare, Search as SearchIcon, FileText, Users, ClipboardList, Book } from 'lucide-react';
import { getAllExams } from '../utils/examAPI';

/**
 * 获取所有考试的信息（题目数和时长）
 * @param {Array} exams - 考试列表
 * @returns {Object} 包含题目数和时长的对象
 */
const getExamsInfoBatch = async (exams) => {
  try {
    const totals = {};
    const durations = {};
    
    exams.forEach(exam => {
      totals[exam._id] = exam.count || 0;
      durations[exam._id] = exam.duration || 0;
    });
    
    return { totals, durations };
  } catch (error) {
    console.error('获取考试信息批量错误:', error);
    return { totals: {}, durations: {} };
  }
};

function Exam({ session }) {
  const [exams, setExams] = useState([]);
  const [examTotals, setExamTotals] = useState({});
  const [examDurations, setExamDurations] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchExams = async () => {
      try {
        setLoading(true);
        
        // 检查用户身份
        if (!session && !localStorage.getItem('user_id')) {
          navigate('/login');
          return;
        }
        
        // 获取考试列表
        const data = await getAllExams();
        
        // 批量获取每个考试的总题目数和时长
        const { totals, durations } = await getExamsInfoBatch(data);
        
        setExams(data || []);
        setExamTotals(totals);
        setExamDurations(durations);
        
      } catch (error) {
        console.error('Error fetching exams:', error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchExams();
  }, [session, navigate]);

  return (
    <div className="pb-24 app-bg-color">
      <Header title="模拟考试中心" showBack={false} />
      
      <div className="p-4">
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h3 className="font-semibold text-lg text-gray-800 mb-2">练习说明</h3>
          <p className="text-gray-600 text-sm">
            1. 本系统提供考前练习和模拟考试，帮助您熟悉考试内容<br />
            2. 模拟考试会限时，答题环境接近真实考试<br />
            3. 系统会自动判分并提供详细的练习报告，帮助您找出薄弱环节<br />
            4. 反复练习可提高通过官方考试的几率
          </p>
        </div>
        
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <BookOpen className="w-5 h-5 text-primary-dark mr-2" />
          可用模拟考试
        </h2>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {exams.length > 0 ? (
              exams.map((exam) => (
                <div key={exam._id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-800">{exam.name}</h3>
                    <span className="px-3 py-1 text-xs bg-blue-100 text-blue-500 rounded-full">
                      {exam.difficulty || '模拟练习'}
                    </span>
                  </div>
                  
                  <div className="flex text-sm text-gray-500 mb-3 space-x-4">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      <span>{examDurations[exam._id] || exam.duration || '未知'}分钟</span>
                    </div>
                    <div className="flex items-center">
                      <FileText className="w-4 h-4 mr-1" />
                      <span>{examTotals[exam._id] || exam.count || '未知'}题</span>
                    </div>
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      <span>练习模式</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between mt-3">
                    <Link
                      to={`/question-practice/${exam._id}`}
                      className="px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm hover:bg-blue-100 transition-all"
                    >
                      自由练习
                    </Link>
                    
                    <Link
                      to={`/exam-start/${exam._id}`}
                      className="px-4 py-2 bg-gradient-to-r from-primary-dark to-primary text-white rounded-full text-sm shadow hover:shadow-md transition-all"
                    >
                      开始模拟考试
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>暂无模拟考试</p>
                <p className="text-xs mt-2 text-gray-400">您可以访问题库页面进行自由练习</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      <TabBar />
    </div>
  );
}

export default Exam; 